<template>
  <div class="bg-white dark:bg-black relative w-screen h-full flex flex-wrap flex-row-reverse md:flex-nowrap md:grid md:grid-cols-12">
    <div v-if="screen.md && tenantBounds.length > 0" class="relative w-full md:w-fill h-full md:col-span-8 xl:col-span-9">
      <Suspense>
        <template #default>
          <MapBoxWrapper
            :tenant-bounds="tenantBounds"
            :highlighted-site="highlightedSite"
            :highlighted-region="highlightedRegion"
            :alerts="alerts"
            :side-menu-btn-clicked="sideMenuBtnClicked"
          />
        </template>
        <template #fallback>
          <div class="h-full w-full relative flex flex-col justify-center items-center">
            <font-awesome-icon icon="fa-solid fa-location-arrow" class="h-10 w-10 text-gray-600 animate-pulse mb-2" />
            <div class="flex items-center justify-center text-gray-500 dark:text-gray-400">
              Loading map...
            </div>
          </div>
        </template>
      </Suspense>
    </div>
    <!-- SIDEBAR SITE LIST -->
    <ul role="list" class="bg-white dark:bg-black md:overflow-y-auto underHeaderHeight md:fixed md:right-0 md:top-12 w-full md:w-4/12 xl:w-3/12 z-10">
      <div class="p-0.5">
        <input
          class="border-gray-300 dark:border-gray-800 w-full text-sm focus:outline-none shadow-sm focus:shadow focus:border-gray-400 focus:ring-1 focus:ring-black focus:ring-opacity-5 rounded transition-all"
          v-model="search"
          type="search"
          name="search"
          placeholder="Search"
          autocomplete="off"
        >
      </div>
      <SiteMapRegionList
        v-if="filteredGlobalRegion"
        :region="filteredGlobalRegion"
        :screen="screen"
        :alerts="alerts"
        :get-sites-obj="getSitesObj"
        :site-weather="siteWeather"
        :levels="[{
          id: filteredGlobalRegion.id,
          region_name: filteredGlobalRegion.region_name
        }]"
        @highlight-region="highlightRegion"
        @clear-region-highlight="clearRegionHighlight"
        @click-region="clickRegion"
        @highlight-site="highlightSite"
        @clear-site-highlight="clearSiteHighlight"
        @focus-site="focusSite"
        @navigate-to-site="navigateToSite"
      />
      <p v-else class="p-4 mt-6 text-sm text-gray-500 dark:text-gray-400">No sites found</p>
    </ul>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue"
import { mapGetters, mapMutations, mapState } from "vuex"
import SiteMapRegionList from "./SiteMap/SiteMapRegionList.vue"
import { getAlertsForSites } from "@/services/alert.services"
import { getWeather } from "@/services/weather.service"

export default {
  name: "SiteMap",
  components: {
    SiteMapRegionList,
    MapBoxWrapper: defineAsyncComponent(() => import("./SiteMap/MapBoxWrapper.vue"))
  },
  data() {
    return {
      loading: false,
      sites: [],
      tenantBounds: [],
      search: "",
      alerts: {},
      sideMenuBtnClicked: null,
      descendantRegions: {},
      highlightedSite: null,
      highlightedRegion: null,
      siteWeather: {}
    }
  },
  computed: {
    ...mapState([
      "headers",
      "active_site",
      "screen",
      "orientation"
    ]),
    ...mapGetters([
      "getEmployeeSites",
      "getActiveSite",
      "getUserId",
      "getActiveTenantId",
      "getGlobalRegion",
      "getSitesObj",
      "getRegionsObj"
    ]),
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    isMobilePortrait() {
      return this.isMobile && this.orientation.portrait
    },
    globalRegionWithSites() {
      const checkRegion = (region) => {
        let directSites = []
        if (Array.isArray(region.child_sites)) {
          directSites = region.child_sites.filter(site => this.getSitesObj[site.id]?.active)
        }

        let subregions = []
        let subregionSiteCount = 0

        if (Array.isArray(region.child_regions)) {
          subregions = region.child_regions
            .map(checkRegion)
            .filter(sub => sub.total_site_count > 0)

          // Sum active sites in subregions
          subregionSiteCount = subregions.reduce((acc, sub) => acc + sub.total_site_count, 0)
        }

        const directCount = directSites.length
        const totalCount = directCount + subregionSiteCount

        return {
          ...region,
          child_sites: directSites,
          child_regions: subregions,
          direct_site_count: directCount,
          subregion_site_count: subregionSiteCount,
          total_site_count: totalCount
        }
      }

      return this.getGlobalRegion ? checkRegion(this.getGlobalRegion) : null
    },
    filteredGlobalRegion() {
      const search = this.search?.trim().toLowerCase()
      if (!search) return this.globalRegionWithSites

      const filterRegion = (region) => {
        const regionMatches = region.region_name?.toLowerCase().includes(search)

        // Filter sites
        const matchingSites = (region.child_sites || []).filter(site =>
          site.name?.toLowerCase().includes(search)
        )

        // Recursively filter child regions
        const matchingSubregions = (region.child_regions || [])
          .map(filterRegion)
          .filter(Boolean)

        // If the region name matches, return it with all children
        if (regionMatches) {
          return {
            ...region,
            // Keep all child sites and child regions if region name matches
            child_sites: region.child_sites || [],
            child_regions: region.child_regions || []
          }
        }

        // Otherwise, only include matching children
        if (matchingSites.length > 0 || matchingSubregions.length > 0) {
          return {
            ...region,
            child_sites: matchingSites,
            child_regions: matchingSubregions
          }
        }

        return null
      }

      return filterRegion(this.globalRegionWithSites)

    },
  },
  watch: {
    getEmployeeSites(val, oldVal) {
      if (val !== oldVal) {
        console.log("new sites", val)
        this.sites = []
        this.tenantBounds = []
        this.loading = true
        this.buildSitesList()
      }
    }
  },
  async mounted() {
    await this.buildSitesList();
  },
  methods: {
    ...mapMutations([
      "closeModal",
      "closePrompt",
      "setPrompt",
      "setScreenSize",
      "setOrientation",
      "setRouteBeforeForcedLogout",
      "setEmployeeSites",
      "setActiveSite",
      "setPermissionSet",
      // "setEmployeeId",
      // "setSiteId",
      "setPOS_System"
    ]),
    findIndexByProperty(array, property, value) {
      return array.findIndex(item => item[property] === value);
    },
    async buildSitesList() {
      await this.getEmployeeSites.forEach(site => {
        if (site.active) {
          this.sites.push(site);
          this.tenantBounds.push([site.longitude, site.latitude]);
        }
      })
      this.loading = false
      this.loadAlerts()
      this.loadAllSiteWeather()
    },
    async navigateToSite(site_id) {
      // this.setSiteId(site_id)
      const fullSiteObj = this.getSitesObj[site_id]
      // this.setUserTeamID(this.sites[index].site_team_id)
      this.setActiveSite({
        "site_id": fullSiteObj.site_id,
        "site_team_id": fullSiteObj.site_team_id,
        "id": fullSiteObj.id,
        "name": fullSiteObj.name,
        "address": fullSiteObj.address,
        "zip_code": fullSiteObj.zip_code,
        "latitude": fullSiteObj.latitude,
        "longitude": fullSiteObj.longitude,
        "pos_system": fullSiteObj.pos_system
      });
      // this.setEmployeeId(fullSiteObj.id)
      this.setPermissionSet(fullSiteObj.permissions)
      this.setPOS_System(fullSiteObj.pos_system)
      await this.$router.push('/')
    },
    highlightSite(site_id) {
      this.highlightedSite = site_id
    },
    clearSiteHighlight(site_id) {
      if (this.sideMenuBtnClicked === site_id) return;
      this.highlightedSite = null
    },
    focusSite(site_id) {
      if (this.sideMenuBtnClicked === site_id) this.sideMenuBtnClicked = null
      this.sideMenuBtnClicked = site_id
    },
    clickRegion(regionId) {
      if (this.sideMenuBtnClicked === regionId) this.sideMenuBtnClicked = null
      this.sideMenuBtnClicked = regionId
      this.highlightRegion(regionId)
    },
    highlightRegion(regionID) {
      this.highlightedRegion = regionID
    },
    clearRegionHighlight(regionId) {
      if (this.sideMenuBtnClicked === regionId) return
      this.highlightedRegion = null
    },
    async loadAlerts() {
      let headers = { ...this.headers }
      if (!headers.siteId) {
        headers.siteId = this.sites.length > 0 ? this.sites[0].site_id : null
      }
      console.log(headers)
      await getAlertsForSites(this.sites.map(x => x.site_id), headers)
          .then(response => {
            if (response.status === 200) {
              let alerts = {}
              for (let site of Object.keys(response.data.results)) {
                alerts[site] = response.data.results[site].filter(x => !x.acknowledged).length
              }
              this.alerts = alerts
            }
            else {
              console.log(response)
            }
          }).catch(error => {
        console.log(error)
      })
    },
    async loadAllSiteWeather() {
      for (const site of this.sites) {
        try {
          const data = await getWeather(site.site_id, site.latitude, site.longitude)
          if (data?.main) {
            this.siteWeather = { ...this.siteWeather, [site.site_id]: data }
          }
        } catch (e) {
          // silently skip
        }
      }
    },
    getAllDescendantRegionIds(regionId) {
      if (this.descendantRegions[regionId]) {
        return this.descendantRegions[regionId]
      }

      const region = this.getRegionsObj[regionId]
      if (!region) return []

      const childRegions = region.child_regions || []
      const childIds = childRegions.map(r => r.id)
      const nestedIds = childIds.flatMap(id => this.getAllDescendantRegionIds(id))

      const allDescendants = [regionId, ...nestedIds]
      this.descendantRegions[regionId] = allDescendants

      return allDescendants
    },
  },
}
</script>